"use client";

import { cn } from "@/components/ui/cn";
import { useState, useRef, useEffect } from "react";
import { useDismissable } from "@/lib/hooks/useDismissable";
import { useHideOnScroll } from "@/lib/hooks/useHideOnScroll";
import { useModalSurface } from "@/lib/hooks/useModalSurface";
import { useBodyScrollLock } from "@/lib/hooks/useBodyScrollLock";
import { usePresence } from "@/lib/hooks/usePresence";
import { useSiteSearch } from "@/lib/hooks/useSiteSearch";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useCurrentUser, useMe } from "@/lib/hooks/useCurrentUser";
import { useFeedback } from "@/components/FeedbackButton";
import { useNotifications, useUnreadNotificationCount, useMarkNotificationRead, useMarkAllNotificationsRead, notificationsOf } from "@/lib/hooks/useNotifications";
import { CountBadge, Dot, UNREAD_ROW_BG, UnreadSlot } from "@/components/UnreadMarker";
import mu from "@/lib/i18n/messages/cUnread";
import { useUnreadMessageCount } from "@/lib/hooks/useConversations";
import { DmBadge } from "@/components/DmSignal";
import { MessagesDropdown } from "@/components/MessagesDropdown";
import { TimeAgo } from "./TimeAgo";
import { useLogout } from "@/lib/hooks/useLogout";
import { Avatar } from "./ui/Avatar";
import { OverlayScrollArea, PANEL_INSET } from "./ui/OverlayScrollArea";
import { DropdownHeader } from "./ui/DropdownHeader";
import { HEADER_ICON_NOT_READY_CLASS, headerAvatarButtonClass, headerAvatarRingClass, headerIconClass } from "./ui/headerIcon";
import { ADMIN_PATH, BellIcon, BulbIcon, INVITE_PATHS, MenuIcon, SearchIcon, MessagesIcon } from "./HeaderIcons";
import { NOT_READY_CLASS, READY_IN_CLASS, READY_TRANSITION_CLASS } from "./ui/notReady";
import { AvatarStack } from "./AvatarStack";
import { Input } from "./ui/Input";
import { SearchResultRow } from "./SearchResultRow";
import { isComposerRoute } from "@/lib/groupSlug";
import { isStandalonePage } from "@/lib/standalonePages";
import { useT } from "@/lib/i18n/client";
import m from "@/lib/i18n/messages/cSiteHeader";
import common from "@/lib/i18n/messages/common";
import feedbackMessages from "@/lib/i18n/messages/cFeedback";
import inviterMessages from "@/lib/i18n/messages/inviter";
import { SideMenu } from "@/components/SideMenu";
import { Loader } from "@/components/ui/Loading";
import searchMessages from "@/lib/i18n/messages/search";
import { errorText } from "@/lib/errors";
import { isThetaError } from "@theta/api-client";

/**
 * Routes that carry their OWN Theta mark and must not get the bar's as well.
 *
 * These are the standalone token-landing screens: an invitation, an open
 * invite link, an activation link. Each is a page somebody arrives at from an
 * e-mail with one thing to do, and each renders the ident through
 * `CenteredCard` — so the bar's small mark above it was the same logo twice.
 * The bar's "Log ind" is no loss either: `InviteLinkClient` already handles
 * the signed-in and the has-an-account reader inside the card.
 */
const HIDDEN_PATHS = ["/login", "/adgang", "/tilmelding", "/invitationer", "/u/activate-account", "/u/password-reset"];

/** A row of the avatar menu, and its 16px line icon. */
const MENU_ROW = "flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors";
function MenuRowIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-subtle-foreground">
      {children}
    </svg>
  );
}

/** How long the drawer stays on screen sliding out — `.animate-slide-out-left`. */
const DRAWER_EXIT_MS = 200;

/** The header dropdown's preview: up to this many groups, posts and events each. */
const HEADER_RESULTS_PER_TYPE = 4;

/** The link at the foot of a header dropdown: "Se alle", "Vis alle resultater". */
const DROPDOWN_FOOTER_LINK =
  "block px-4 py-2.5 text-sm text-center text-primary hover:bg-muted transition-colors";

export function SiteHeader() {
  const t = useT(m);
  const tu = useT(mu);
  const tc = useT(common);
  const pathname = usePathname();
  const { data: user, isLoading } = useCurrentUser();
  const unreadCount = useUnreadNotificationCount();
  const unreadMessages = useUnreadMessageCount();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  /**
   * Outside-click and Escape, like every dropdown. Escape PUTS FOCUS BACK ON
   * THE TRIGGER: without that a keyboard reader who dismisses the menu is
   * returned to the top of the document and has to tab the whole header
   * again — the same cost the skip link exists to remove.
   */
  const avatarButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useDismissable<HTMLDivElement>(dropdownOpen, () => setDropdownOpen(false), {
    returnFocus: avatarButtonRef,
  });

  const [notifOpen, setNotifOpen] = useState(false);
  const bellButtonRef = useRef<HTMLButtonElement>(null);
  const notifRef = useDismissable<HTMLDivElement>(notifOpen, () => setNotifOpen(false), {
    returnFocus: bellButtonRef,
  });
  // Signed out, there is no bell and no answer: the route 401s for an
  // anonymous caller, so polling it is five requests a minute that cannot
  // succeed (OSO-575).
  const { data: notifData } = useNotifications({ enabled: !!user });
  const notifications = notificationsOf(notifData);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const drawer = usePresence(sidebarOpen, DRAWER_EXIT_MS);
  /**
   * THE PRIMARY NAVIGATION ON A PHONE, AND IT WAS NOT A DIALOG (OSO-638).
   * Enter on the hamburger opened the drawer and left focus on the hamburger,
   * OUTSIDE it: Tab then walked into the page behind the backdrop — the Theta
   * logo, the header links — and never once into the drawer, and Escape did
   * nothing at all. The only dismissal was a tap on the backdrop `div`, which
   * has no keyboard equivalent.
   *
   * The hook supplies the whole contract, including returning focus to the
   * hamburger on close. It is the same one `ui/Dialog` uses; the drawer keeps
   * its own chrome because a full-height edge-anchored panel is not a centred
   * box with different padding.
   */
  const drawerRef = useModalSurface<HTMLElement>({
    open: sidebarOpen,
    onClose: () => setSidebarOpen(false),
  });
  /**
   * THE PAGE BEHIND HOLDS STILL while the drawer is open. A swipe in the menu
   * that reached the page scrolled it, and on a phone that slid the browser's
   * address bar away — the screen grew under the drawer and showed a band of
   * page beneath it until the drawer caught up.
   */
  useBodyScrollLock(sidebarOpen);

  /**
   * A DRAWER THAT IS NO LONGER DRAWN MUST NOT STILL HOLD THE KEYBOARD.
   *
   * The panel is `lg:hidden`, so widening the window past 1024px — a rotation,
   * a split-screen change, dragging a desktop window wider — leaves it
   * `display: none` while `sidebarOpen` is still true. The trap keeps running
   * over an invisible panel: focus is on `body`, so every Tab is swallowed and
   * redirected into a hidden element that cannot take it, and keyboard
   * navigation dies page-wide with nothing on screen to explain why. Only a
   * blind Escape recovers it.
   *
   * `matchMedia` and not a resize listener: this fires twice per crossing
   * rather than on every pixel of a drag. `lg` is Tailwind's 64rem, spelled
   * here because CSS is the only other place it exists.
   */
  useEffect(() => {
    if (!sidebarOpen) return;
    // `matchMedia` can be absent or throw in an old embedded WebView — the
    // same hazard `app/layout.tsx`'s boot script guards for. Losing this
    // cleanup there means the drawer behaves exactly as it did before this
    // effect existed, which is the right way to lose it.
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(min-width: 64rem)");
    if (mq.matches) {
      // Already past the breakpoint when the effect runs — the drawer was
      // opened on a phone and the window was resized before this mounted, or
      // React re-ran the effect after one. Closing here is the whole point of
      // the effect rather than a state-sync smell: the alternative is a panel
      // that is not drawn and still eats every Tab.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSidebarOpen(false);
      return;
    }
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setSidebarOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [sidebarOpen]);
  // Inside the native app only (OSO-747). 56 = the bar's `h-14`.
  const scrolledAway = useHideOnScroll(56, {
    pinned: dropdownOpen || notifOpen || sidebarOpen,
    resetKey: pathname,
  });

  const logout = useLogout();
  const { data: me } = useMe();
  const { openFeedback, feedbackAvailable } = useFeedback();
  const tf = useT(feedbackMessages);
  const tSearch = useT(searchMessages);
  const ti = useT(inviterMessages);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDismissed, setSearchDismissed] = useState(false);
  // `GET /search` through the same hook and row as `/soeg`, so the two cannot
  // disagree about what a result is or where it goes — but a few of EACH kind
  // rather than one ranked top twenty, which a group name always won. No
  // headings: every row says its own kind (`SearchResultRow`).
  const {
    results: searchResults,
    isSearching: searchRunning,
    isSlow: searchSlow,
    failure: searchFailure,
  } = useSiteSearch(searchQuery, undefined, {
    perType: HEADER_RESULTS_PER_TYPE,
  });
  /** Still going after a second, with nothing to show yet: "Søger…" in the dropdown. */
  const searchWaiting = searchResults.length === 0 && searchSlow && !searchFailure;
  /** Finished, and nothing matched: said so, as /soeg says it. */
  const searchEmpty =
    searchQuery.trim().length > 0 && !searchRunning && searchFailure == null && searchResults.length === 0;
  const searchOpen =
    !searchDismissed && (searchResults.length > 0 || searchWaiting || searchFailure != null || searchEmpty);
  /**
   * THE ARROW KEYS' HIGHLIGHT (OSO-739), remembered together with the list it
   * was made in: a new list — a new term, or the debounced answer arriving —
   * starts with nothing highlighted rather than the same row number in
   * different results. Closing the list and typing also clear it, because a
   * term typed again comes back from the cache as the SAME list, and a
   * leftover highlight would turn an Enter meant to do nothing into a jump.
   */
  const router = useRouter();
  const listKey = searchResults.map((r) => `${r.resultType}-${r.id}`).join();
  const [highlight, setHighlight] = useState({ list: "", index: -1 });
  const activeIndex = highlight.list === listKey ? highlight.index : -1;
  const optionId = (i: number) => `soegeforslag-${i}`;
  const clearHighlight = () => setHighlight({ list: "", index: -1 });

  function dismissSearch() {
    setSearchDismissed(true);
    clearHighlight();
  }
  const searchRef = useDismissable<HTMLDivElement>(searchOpen, dismissSearch, {
    escape: false,
  });

  function closeSearch() {
    dismissSearch();
    setSearchQuery("");
  }

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    // Safari ends an IME composition with isComposing already false.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (searchResults.length === 0) return;
      e.preventDefault();
      if (!searchOpen) {
        setSearchDismissed(false);
        return;
      }
      const n = searchResults.length;
      // From nothing highlighted, Down takes the first row and Up the last.
      const next = e.key === "ArrowDown" ? (activeIndex + 1) % n : (activeIndex <= 0 ? n : activeIndex) - 1;
      setHighlight({ list: listKey, index: next });
      document.getElementById(optionId(next))?.scrollIntoView?.({ block: "nearest" });
    } else if (e.key === "Enter" && searchOpen && activeIndex >= 0) {
      e.preventDefault();
      const href = searchResults[activeIndex].permalink;
      if (e.metaKey || e.ctrlKey) window.open(href, "_blank");
      else router.push(href);
      closeSearch();
    } else if (e.key === "Escape" && searchOpen) {
      e.preventDefault();
      dismissSearch();
    }
  }

  // Close the sidebar on navigation. Adjusted during render rather than in an
  // effect so it's already closed in the commit that paints the new route —
  // an effect closes it one render later, briefly showing the new page with
  // the old sidebar still open.
  const [prevPathname, setPrevPathname] = useState(pathname);
  if (prevPathname !== pathname) {
    setPrevPathname(pathname);
    setSidebarOpen(false);
  }

  function handleSearchChange(value: string) {
    setSearchQuery(value);
    setSearchDismissed(false);
    clearHighlight();
  }

  if (HIDDEN_PATHS.some((p) => pathname.startsWith(p)) || isComposerRoute(pathname) || isStandalonePage(pathname)) return null;

  async function handleLogout() {
    setDropdownOpen(false);
    await logout();
  }

  const avatarUrl = user
    ? user.avatar_template.replace("{size}", "48")
    : undefined;
  /** On a phone the avatar is a link to your profile, so it is "the page you
   *  are on" there — marked like the other header links. */
  const onOwnProfile = !!user && pathname === `/profil/${user.id}`;

  // Ensure avatar URL is absolute or proxied
  const resolvedAvatar =
    avatarUrl && avatarUrl.startsWith("//")
      ? `http:${avatarUrl}`
      : avatarUrl ?? undefined;

  return (
    <>
    <header
      // Fully shown or fully hidden, and only the change between is animated
      // (`useHideOnScroll`) — in the app and in a phone's browser alike.
      className={`header-gutter-edge bg-surface border-b border-border sticky top-0 z-50 transition-transform duration-200 ease-out motion-reduce:transition-none native-pushed:hidden ${scrolledAway ? "-translate-y-full" : ""}`}
    >
      <div className="relative max-w-[1400px] mx-auto px-4 flex items-center h-14 gap-4 max-[359px]:gap-2">
        {/* Burger and logo share the right-hand icons' spacing, so on a phone
            the header reads as two evenly spaced groups of 40px buttons. */}
        <div className="flex items-center gap-1 sm:gap-3 shrink-0">
        {/* Also drawn while the session is loading, in its final place, so
            the header does not shift when it arrives — but not pressable
            until there is a user to open the menu for. */}
        {(user || isLoading) && (
          <button
            className={`lg:hidden p-2.5 -ml-2 rounded-lg ${READY_TRANSITION_CLASS} ${user ? "text-muted-foreground hover:text-foreground hover:bg-muted" : HEADER_ICON_NOT_READY_CLASS}`}
            onClick={() => setSidebarOpen(true)}
            disabled={!user}
            aria-label={t.menu}
          >
            <MenuIcon />
          </button>
        )}

        {/* Logo — icon always visible, "Theta" text slides in on hover, IN
            THE DESKTOP LAYOUT ONLY (`lg:group-hover`). Tailwind already limits
            hover to devices that have one, but a narrow window with a mouse —
            or Chrome's phone simulation — has one, and there the wordmark slid
            in beside the burger in the phone layout.
            Inside the native app there is no hover, so the wordmark is always
            there, in the login page's gradient (OSO-747).

            It is the only Home link, on a phone and a desktop alike — there is
            no house button in the header and no Hjem row in the menu. On a
            phone it is padded to the same 40px box as the icons beside it. */}
        <Link
          href="/feed"
          className="shrink-0 group flex items-center gap-0 overflow-hidden p-1.5 lg:p-0 native:p-0"
        >
          <img
            src="/favicon.png"
            alt="Theta" // i18n-ignore: product name
            className="w-7 h-7 shrink-0 dark:invert"
          />
          <span
            className="text-2xl tracking-tight text-foreground max-w-0 lg:group-hover:max-w-[120px] overflow-hidden whitespace-nowrap transition-all duration-300 ease-in-out opacity-0 lg:group-hover:opacity-100 ml-0 lg:group-hover:ml-1.5 native:max-w-[120px] native:opacity-100 native:ml-1.5 native:bg-gradient-to-r native:from-indigo-500 native:via-purple-500 native:to-pink-500 native:bg-clip-text native:text-transparent"
            style={{ fontFamily: "Neureal, sans-serif" }}
          >
            Theta{/* i18n-ignore: product name */}
          </span>
        </Link>
        </div>

        {/* Search — desktop only.

            OUT OF THE FLEX FLOW, because the header's two sides are not the
            same width and never will be: `mx-auto` on a flex child centres it
            in the slack LEFT OVER in the row, not in the header, so the field
            sat off-centre — and the logo's wordmark animating
            `max-w-0 → max-w-[120px]` on hover widened the left block, sliding
            the field sideways while the pointer rested on the logo (OSO-488).
            Centring against the header itself is immune to both. Still the
            positioned ancestor the results dropdown below anchors to.

            SIGNED IN ONLY (OSO-498). `GET /search` answers for the viewer, so
            to a logged-out visitor the field promised a search it could not
            run. The mobile icon below was already inside the `user` branch;
            this one sat outside it, which is the whole bug. Drawn but
            disabled while the session loads, like the icons, so the header
            does not grow a field when it arrives. */}
        {(user || isLoading) && (
        <div
          className={`hidden lg:block absolute left-1/2 -translate-x-1/2 w-full max-w-md ${READY_TRANSITION_CLASS} ${user ? "" : NOT_READY_CLASS}`}
          ref={searchRef}
        >
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle-foreground"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <Input
              type="search"
              name="q"
              autoComplete="off"
              placeholder={t.searchPlaceholder}
              disabled={!user}
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => setSearchDismissed(false)}
              onKeyDown={handleSearchKeyDown}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={searchOpen}
              aria-controls={searchOpen ? "soegeforslag" : undefined}
              aria-activedescendant={searchOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined}
              className="w-full pl-9 pr-3 py-2 text-sm bg-muted dark:text-gray-100 rounded-full border border-transparent focus:border-indigo-300 dark:focus:border-indigo-600 focus:bg-surface focus:outline-none transition-colors"
            />
          </div>
          {searchOpen && (
            // The site's own scrollbar, as in every dropdown; the list itself
            // is the listbox the field points to.
            <div className="absolute top-full mt-1 left-0 right-0 bg-surface rounded-xl border border-border shadow-lg z-50 max-h-60 sm:max-h-80 flex flex-col overflow-hidden">
              <OverlayScrollArea className="min-h-0 flex-1" inset={PANEL_INSET} thumbClassName="right-0.5">
              {/* The search times its own second (`useSiteSearch`). */}
              {searchWaiting && <Loader fill={false} late label={tSearch.searching} className="py-6" />}
              {/* One status line, there from the start, so a screen reader
                  reads what lands in it (one that appears with its text often
                  is not). Did not answer — said as it is, never as "no
                  results" (OSO-1117). */}
              <p
                role="status"
                className={cn(
                  searchFailure == null && !searchEmpty ? "sr-only" : "px-4 py-3 text-sm",
                  searchFailure != null ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {searchFailure != null
                  ? isThetaError(searchFailure, "searchTimedOut")
                    ? errorText(searchFailure, tSearch.failed)
                    : tSearch.failed
                  : searchEmpty
                    ? tSearch.noResults(searchQuery.trim())
                    : null}
              </p>
              <div id="soegeforslag" role="listbox" aria-label={t.searchSuggestions}>
              {searchResults.map((result, i) => (
                <SearchResultRow
                  key={`${result.resultType}-${result.id}`}
                  result={result}
                  option={{
                    id: optionId(i),
                    selected: i === activeIndex,
                    onHover: () => setHighlight({ list: listKey, index: i }),
                  }}
                  onNavigate={closeSearch}
                />
              ))}
              </div>
              </OverlayScrollArea>
              {/* "Vis alle resultater" only where there are, or may be, some. */}
              {(searchResults.length > 0 || searchWaiting) && (
              <div className="border-t border-border shrink-0">
                <Link
                  href={`/soeg?q=${encodeURIComponent(searchQuery.trim())}`}
                  onClick={closeSearch}
                  className={DROPDOWN_FOOTER_LINK}
                >
                  {t.searchShowAll}
                </Link>
              </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* The live icons replace the dimmed placeholders below as different
            elements, so they fade up from the placeholders' 40% instead. */}
        {/* Five 40px icons since Giv feedback joined them: on a 320px phone they
            stand edge to edge, so the last one is not cut off. */}
        <div className={`flex items-center gap-1 max-[359px]:gap-0 sm:gap-3 shrink-0 ml-auto ${isLoading ? "" : READY_IN_CLASS}`}>
          {isLoading ? (
            /* THE HEADER AS IT WILL BE, WHILE THE SESSION LOADS. Every icon
               in its final box — same element, padding and visibility classes
               as the live ones below — so nothing moves when they arrive; the
               profile picture is a grey pulsing circle. Not interactive, and
               hidden from screen readers: there is nothing to press yet. */
            <>
              <span aria-hidden className={`lg:hidden p-2.5 rounded-lg ${HEADER_ICON_NOT_READY_CLASS}`}>
                <SearchIcon />
              </span>
              <span aria-hidden className={`lg:hidden native:hidden relative p-2.5 rounded-lg ${HEADER_ICON_NOT_READY_CLASS}`}>
                <MessagesIcon />
              </span>
              <div aria-hidden className="hidden lg:block native:hidden relative">
                <span className={`relative flex p-2 rounded-lg ${HEADER_ICON_NOT_READY_CLASS}`}>
                  <MessagesIcon />
                </span>
              </div>
              <span aria-hidden className={`lg:hidden native:hidden relative p-2.5 rounded-lg ${HEADER_ICON_NOT_READY_CLASS}`}>
                <BellIcon />
              </span>
              <div aria-hidden className="hidden lg:block native:hidden relative">
                <span className={`relative flex p-2 rounded-lg ${HEADER_ICON_NOT_READY_CLASS}`}>
                  <BellIcon />
                </span>
              </div>
              <span aria-hidden className={`flex native:hidden p-2.5 lg:p-2 rounded-lg ${HEADER_ICON_NOT_READY_CLASS}`}>
                <BulbIcon />
              </span>
              {/* The face's button, not ready: the same grey as the dimmed
                  icons beside it — their colour (`text-muted-foreground`) as a
                  fill, at their 40%. High contrast would drop the fill, so
                  there it is drawn in GrayText, the icons' "disabled" colour. */}
              <span aria-hidden className={`native:hidden rounded-full p-1 ${NOT_READY_CLASS}`}>
                <span className="block w-8 h-8 rounded-full bg-muted-foreground forced-color-adjust-none forced-colors:bg-[color:GrayText]" />
              </span>
            </>
          ) : user ? (
            <>
            <Link
              href="/soeg"
              aria-current={pathname === "/soeg" ? "page" : undefined}
              className={`lg:hidden p-2.5 ${headerIconClass(pathname === "/soeg")} rounded-lg`}
              title={tc.search}
            >
              <SearchIcon />
            </Link>

            <Link
              href="/beskeder"
              aria-current={pathname.startsWith("/beskeder") ? "page" : undefined}
              className={`lg:hidden native:hidden relative p-2.5 ${headerIconClass(pathname.startsWith("/beskeder"))} rounded-lg`}
              title={t.messages}
            >
              <MessagesIcon />
              <DmBadge count={unreadMessages} className="absolute -top-0.5 -right-0.5" />
            </Link>

            <MessagesDropdown unreadMessages={unreadMessages} />

            {/* Notification bell — mobile: link, desktop: dropdown */}
            <Link
              href="/notifikationer"
              aria-current={pathname === "/notifikationer" ? "page" : undefined}
              className={`lg:hidden native:hidden relative p-2.5 ${headerIconClass(pathname === "/notifikationer")} rounded-lg`}
              title={t.notifications}
            >
              <BellIcon />
              <CountBadge count={unreadCount} className="absolute -top-0.5 -right-0.5" />
            </Link>

            <div className="hidden lg:block native:hidden relative" ref={notifRef}>
              <button
                ref={bellButtonRef}
                onClick={() => setNotifOpen((prev) => !prev)}
                aria-expanded={notifOpen}
                className={`relative p-2 ${headerIconClass(notifOpen)} rounded-lg`}
                title={t.notifications}
              >
                <BellIcon />
                <CountBadge count={unreadCount} className="absolute -top-0.5 -right-0.5" />
              </button>

              {notifOpen && (
                /* w-96, not w-80 (OSO-605). The panel only ever opens at `lg`
                   and up, and 64px of extra room is what takes the longest
                   sentences — the ones naming three people — from three
                   wrapped lines down to two. */
                /* AS TALL AS THE WINDOW ALLOWS, STOPPING ABOVE "GIV FEEDBACK".
                   The panel starts 60px down (the 56px header + `mt-1`) and
                   ends 86px from the bottom: the feedback pill sits 24px up
                   and is 38px tall, and 24px of air is left between them.
                   Only the list scrolls — the title row and "Se alle" hold
                   still — and a short list simply ends early. */
                <div className="absolute right-0 top-full mt-1 w-96 max-h-[calc(100dvh-146px)] flex flex-col bg-surface rounded-xl border border-border shadow-lg z-50 overflow-hidden">
                  {/* THE UNREAD TOTAL, SPELLED OUT (OSO-524, OSO-586).
                        It is a count of ROWS now, so it is countable from the
                        list — but this dropdown is capped and ordered by time,
                        so an older unread row can still sit below its fold.
                        Saying the number here means the badge is explicable
                        without the member having to reconcile it against the
                        few rows this panel has space for. */}
                  <DropdownHeader
                    title={
                      <>
                        {t.notifications}
                        {unreadCount > 0 && (
                          <span className="ml-1.5 font-normal text-muted-foreground">
                            {t.unreadInHeading(unreadCount > 99 ? "99+" : unreadCount)}
                          </span>
                        )}
                      </>
                    }
                  >
                    {unreadCount > 0 && (
                      <button
                        onClick={() => markAllRead()}
                        className="shrink-0 whitespace-nowrap text-xs text-primary hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
                      >
                        {t.markAllRead}
                      </button>
                    )}
                  </DropdownHeader>
                  <OverlayScrollArea className="min-h-0 flex-1">
                    {notifications.length === 0 ? (
                      <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                        {t.noNotifications}
                      </p>
                    ) : (
                      /* `n.text` and `n.href` are SERVER-RENDERED (§1.12) —
                         the formatter and the href cascade that used to build
                         them on the client are deleted. */
                      notifications.slice(0, 10).map((n) => (
                        /* UNREAD = THE DOT AND A BOLD TIME-AND-QUOTE LINE; read
                           = neither. No envelope here: flipping read/unread is
                           the full list's job (/notifikationer). */
                        <div
                          key={n.id}
                          className={`flex items-center gap-1 pr-3 hover:bg-muted transition-colors ${!n.isRead ? UNREAD_ROW_BG : ""}`}
                        >
                        <Link
                          href={n.href}
                          onClick={() => {
                            setNotifOpen(false);
                            if (!n.isRead && n.can.markRead) markRead(n.id, n.groupedIds);
                          }}
                          className="flex flex-1 min-w-0 items-center gap-3 pl-4 pr-1 py-3"
                        >
                          <AvatarStack
                            primaryUrl={n.actor?.avatar.small}
                            primaryName={n.actor?.name || "?"}
                            otherUrls={n.otherActors.map((a) => a.avatar.small)}
                            otherNames={n.otherActors.map((a) => a.name ?? "")}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground">{n.text}</p>
                            {/* THE TIME SITS BESIDE THE QUOTE, NOT UNDER IT
                                (OSO-605). Two ornaments, three lines: the
                                sentence already wraps, and giving "13m" a line
                                of its own made every row with an excerpt five
                                lines tall. `min-w-0` on the quote so the
                                truncation still happens there and never eats
                                the timestamp. */}
                            <div className={`flex items-baseline gap-2 mt-0.5 ${!n.isRead ? "font-semibold" : ""}`}>
                              <TimeAgo date={n.createdAt} />
                              {n.excerpt && (
                                <p className="min-w-0 flex-1 text-xs text-subtle-foreground truncate">
                                  &ldquo;{n.excerpt}&rdquo;{/* i18n-ignore: quotation marks */}
                                </p>
                              )}
                            </div>
                          </div>
                        </Link>
                        <UnreadSlot>{!n.isRead && <Dot label={tu.unread} />}</UnreadSlot>
                        </div>
                      ))
                    )}
                  </OverlayScrollArea>
                  <div className="border-t border-border shrink-0">
                    <Link
                      href="/notifikationer"
                      onClick={() => setNotifOpen(false)}
                      className={DROPDOWN_FOOTER_LINK}
                    >
                      {t.seeAll}
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* GIV FEEDBACK: the bulb beside the bell, on every page and at
                every width — its one door on the web. */}
            {feedbackAvailable && (
              <button
                type="button"
                onClick={() => openFeedback()}
                aria-label={tf.giveFeedback}
                title={tf.giveFeedback}
                className={`flex native:hidden p-2.5 lg:p-2 ${headerIconClass(false)} rounded-lg`}
              >
                <BulbIcon />
              </button>
            )}

            {/* Inside the app the dock's last tab is the avatar, so this one
                goes; what only its menu offered moves to the drawer's "Konto"
                section below (OSO-747). */}
            {/* On a phone the avatar goes straight to your profile, like the
                bell and messages icons beside it go straight to their pages.
                Log ud sits at the bottom of your own profile instead. */}
            <Link
              href={`/profil/${user.id}`}
              aria-label={t.myProfile}
              aria-current={onOwnProfile ? "page" : undefined}
              className={`lg:hidden native:hidden ${headerAvatarButtonClass(onOwnProfile)}`}
            >
              <span className={headerAvatarRingClass(onOwnProfile)}>
                <Avatar
                  url={resolvedAvatar}
                  name={user.name || user.username}
                  size="sm"
                />
              </span>
            </Link>
            <div
              className="relative hidden lg:block native:hidden"
              ref={dropdownRef}
            >
              {/* A REAL DISCLOSURE, NOT A HOVER TARGET (OSO-637). Click opens
                  it, the same as messages and notifications — on a desktop this
                  menu is the only route to Min profil, Inviter til Theta,
                  Indstillinger and Log ud, so it must work without a mouse.

                  The accessible name was "S", the letter avatar's initial, so
                  a screen reader announced "S, knap" and pressing it did
                  nothing. `aria-label` names what the button opens instead. */}
              <button
                type="button"
                ref={avatarButtonRef}
                onClick={() => setDropdownOpen((open) => !open)}
                aria-label={t.accountMenu}
                aria-haspopup="menu"
                aria-expanded={dropdownOpen}
                aria-controls="brugermenu"
                className={`flex items-center gap-2 ${headerAvatarButtonClass(dropdownOpen)}`}
              >
                <span className={headerAvatarRingClass(dropdownOpen)}>
                  <Avatar
                    url={resolvedAvatar}
                    name={user.name || user.username}
                    size="sm"
                  />
                </span>
              </button>

              {dropdownOpen && (
                <div id="brugermenu" className="absolute right-0 top-full mt-1 w-56 bg-surface rounded-xl border border-border shadow-lg overflow-hidden z-50">
                  <DropdownHeader title={user.name || user.username} />
                  <Link href={`/profil/${user.id}`} onClick={() => setDropdownOpen(false)} className={MENU_ROW}>
                    <MenuRowIcon>
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </MenuRowIcon>
                    {t.myProfile}
                  </Link>
                  {me?.can.invite && (
                    <Link href="/inviter" onClick={() => setDropdownOpen(false)} className={MENU_ROW}>
                      <MenuRowIcon>{INVITE_PATHS}</MenuRowIcon>
                      {ti.title}
                    </Link>
                  )}
                  <Link href="/indstillinger/konto" onClick={() => setDropdownOpen(false)} className={MENU_ROW}>
                    <MenuRowIcon>
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </MenuRowIcon>
                    {t.settings}
                  </Link>
                  {/* Admin lives here and nowhere else on a computer — the
                      header's shield went (OSO-1014). Giv feedback is the
                      header's bulb, not a row here. */}
                  {user.admin && (
                    <Link href="/admin" onClick={() => setDropdownOpen(false)} className={MENU_ROW}>
                      <MenuRowIcon>{ADMIN_PATH}</MenuRowIcon>
                      {t.admin}
                    </Link>
                  )}
                  <Link href="/om" onClick={() => setDropdownOpen(false)} className={MENU_ROW}>
                    <MenuRowIcon>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </MenuRowIcon>
                    {t.aboutTheta}
                  </Link>
                  <div className="border-t border-border">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-destructive hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      {tc.signOut}
                    </button>
                  </div>
                </div>
              )}
            </div>
            </>
          ) : (
            <Link
              href="/login"
              // Not while the page itself is a login form (a private event's link).
              className="[body:has([data-login-form])_&]:hidden px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              {tc.signIn}
            </Link>
          )}
        </div>
      </div>
    </header>

    {drawer.mounted && (
      <>
        <div
          // While it fades out it must not swallow the next tap on the page.
          className={`lg:hidden fixed inset-0 bg-black/40 z-[60] ${drawer.closing ? "animate-fade-out pointer-events-none" : "animate-fade-in"}`}
          onClick={() => setSidebarOpen(false)}
        />
        {/* ITS GROUND IS AS TALL AS THE SCREEN CAN GET (`lvh`, the address bar
            slid away) and ITS CONTENTS AS TALL AS THE SCREEN YOU SEE (`dvh`,
            the bottom padding is the difference). Sized to what you see, the
            panel left a band of page beneath it while Chrome slid its address
            bar away; sized to the largest, Feedback and Om Theta sat under the
            browser's own bar. The padding changes in one step when the
            address bar has finished sliding, so it is eased: Feedback and Om
            Theta glide to their place instead of jumping. And 1px past the
            bottom:
            When the viewport height is not a whole number of device pixels —
            a zoomed DevTools phone, a mobile browser bar sliding in or out —
            the panel's bottom edge rounds up and left a 1px strip of the page
            showing beneath it. The pixel hanging off-screen is never seen.

            While it slides out the drawer is still on screen but already
            closed: `inert` keeps it from taking taps and focus, and hides it
            from screen readers, for those last 200ms. */}
        <nav
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-label={t.menu}
          tabIndex={-1}
          inert={drawer.closing}
          aria-hidden={drawer.closing || undefined}
          className={`lg:hidden fixed top-0 h-[calc(100lvh+1px)] left-0 w-76 max-w-[85vw] bg-ground z-[60] shadow-xl flex flex-col pb-[calc(100lvh-100dvh)] transition-[padding] duration-300 ease-out motion-reduce:transition-none native:pb-(--native-dock-space) outline-none ${drawer.closing ? "animate-slide-out-left" : "animate-slide-in-left"}`}
        >
          {/* ABOVE the page's own floating things (`z-[60]`, they are `z-50`):
              the "Installer Theta" banner covered Feedback and Om Theta at
              the bottom of the drawer.

              The title row, and its "<" — the same arrow that closes a
              group's admin menu on a computer. Only the arrow closes the
              menu (with the backdrop and Escape): the row itself is not a
              button any more. */}
          {/* 57px with its line INSIDE, so the line sits exactly on the
              header's: that is 56px with its line BELOW. "Theta" closes the
              menu too — a second, larger target for a thumb; the "<" is the
              one a screen reader and the keyboard get. */}
          <div className="flex items-center justify-between pl-4 pr-2.5 h-[3.5625rem] shrink-0 border-b border-border">
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              onClick={() => setSidebarOpen(false)}
              className="text-xl tracking-tight text-foreground"
              style={{ fontFamily: "Neureal, sans-serif" }}
            >
              Theta{/* i18n-ignore: product name */}
            </button>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              aria-label={t.closeMenu}
              title={t.closeMenu}
              className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg border border-transparent text-foreground hover:bg-surface transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m15 6-6 6 6 6" />
              </svg>
            </button>
          </div>
          {/* THE COMPUTER'S MENU, in the drawer — one menu, two places
              (`SideMenu`). Any link in it closes the drawer, the one to the
              page you are already on included; Feedback closes it before its
              dialog opens.

              Inside the app the native dock floats over the bottom of the
              screen (OSO-790): the panel reserves its height and the menu
              scrolls inside what is left. */}
          <div
            className="min-h-0 flex-1 flex flex-col"
            onClickCapture={(e) => {
              // The NEAREST link or button decides: the Admin button sits inside
              // its group's row link, and opening its list must not close this.
              if ((e.target as Element).closest("a[href], button")?.tagName === "A") setSidebarOpen(false);
            }}
          >
            <SideMenu variant="drawer" onLeave={() => setSidebarOpen(false)} />

            {/* INSIDE THE APP ONLY: the avatar menu's destinations. The header
                avatar is hidden there (the dock has it), and Log ud and Admin
                have no other entry point (OSO-747). Om Theta is in the list
                above, for everyone. */}
            {user && (
              <div className="hidden native:block shrink-0 border-t border-border py-2">
                <p className="px-4 pt-1 pb-2 text-xs font-semibold uppercase tracking-wide text-subtle-foreground">
                  {t.account}
                </p>
                <Link href={`/profil/${user.id}`} className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground hover:bg-muted transition-colors">
                  {t.profile}
                </Link>
                <Link href="/indstillinger/konto" className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground hover:bg-muted transition-colors">
                  {t.settings}
                </Link>
                {user.admin && (
                  <Link href="/admin" className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground hover:bg-muted transition-colors">
                    {t.admin}
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium text-destructive hover:bg-destructive-muted transition-colors"
                >
                  {tc.signOut}
                </button>
              </div>
            )}
          </div>
        </nav>
      </>
    )}
    </>
  );
}
